import React from "react";
import { MarketingNav, MarketingFooter } from "../../components/Marketing/MarketingChrome/MarketingChrome";
import AnchorButton from "../../components/OldFront/Simple/AnchorButton/AnchorButton";
import { snakeCaseToCopyText } from "../../../../shared/src/util";
import * as PublicListingsView from "../../../../domain/models/PublicListingsView";
import * as PublicListingCard1 from "../../../../domain/models/PublicListingCard1";

const formatPrice = (card: PublicListingCard1.T): string =>
    card.price === null
        ? "POA"
        : `£${card.price.toLocaleString("en-GB")}`;

const statusLabel = (status: PublicListingCard1.T["status"]): string =>
    status === "sstc" ? "Under offer" : "For sale";

const facts = (card: PublicListingCard1.T): Array<string> => [
    ...(card.bedroom_count !== null ? [`${card.bedroom_count} bed${card.bedroom_count === 1 ? "" : "s"}`] : []),
    ...(card.bathroom_count !== null ? [`${card.bathroom_count} bath${card.bathroom_count === 1 ? "" : "s"}`] : []),
    ...(card.property_type && card.property_type !== "unknown" ? [snakeCaseToCopyText(card.property_type)] : []),
];

const filterUrl = (v: PublicListingsView.T, page: number): string => {
    const p = new URLSearchParams();
    if (v.statusFilter !== "all") { p.set("status", v.statusFilter); }
    if (v.sort !== "recent") { p.set("sort", v.sort); }
    if (v.probateOnly) { p.set("probate", "1"); }
    if (v.search !== "") { p.set("q", v.search); }
    if (page > 1) { p.set("page", String(page)); }
    const qs = p.toString();
    return qs === "" ? "/listings" : `/listings?${qs}`;
};

export const PublicListingsAppContainer = (props: { view: PublicListingsView.T }): JSX.Element => {
    const v = props.view;

    return (
        <div className="marketing publicListings">
            <MarketingNav view={v} />

            <div className="publicListings__body">
                <div className="publicListings__hero">
                    <h1 className="publicListings__h1">Properties for sale</h1>
                    <p className="publicListings__intro">
                        Most of these homes are being sold by executors after a death, so there
                        is usually no chain above you and the property is usually empty.
                    </p>
                    <p className="publicListings__introLink">
                        <a href="/buying">What to expect when you buy a probate property</a>
                    </p>
                    <p className="publicListings__count">
                        {v.meta.available_count === 0
                            ? "No properties match your search."
                            : `Showing ${v.listings.length} of ${v.meta.available_count} properties`}
                    </p>
                </div>

                {/* FILTERS — a plain GET form so it works server-side without JS */}
                <form className="publicListings__filters" method="get" action="/listings">
                    <input
                        className="publicListings__filterSearch"
                        type="text"
                        name="q"
                        placeholder="Search by town or postcode"
                        defaultValue={v.search}
                        aria-label="Search by town or postcode"
                    />
                    <select className="publicListings__filterSelect" name="status" defaultValue={v.statusFilter} aria-label="Status">
                        <option value="all">All properties</option>
                        <option value="for_sale">For sale</option>
                        <option value="under_offer">Under offer</option>
                    </select>
                    <select className="publicListings__filterSelect" name="sort" defaultValue={v.sort} aria-label="Sort by">
                        <option value="recent">Most recent</option>
                        <option value="price_asc">Price (low to high)</option>
                        <option value="price_desc">Price (high to low)</option>
                    </select>
                    <label className="publicListings__filterToggle">
                        <input type="checkbox" name="probate" value="1" defaultChecked={v.probateOnly} />
                        Probate only
                    </label>
                    <button className="publicListings__filterSubmit" type="submit">Search</button>
                </form>

                <div className="publicListings__grid">
                    {v.listings.map((card) => (
                        <div className="publicListings__card" key={card.id}>
                            <a className="publicListings__imgLink" href={`/listings/${card.id}`}>
                                <div className="publicListings__imgWrap">
                                    {card.cover_image_url
                                        ? <img
                                            className="publicListings__img"
                                            src={card.cover_image_url}
                                            alt={card.address_short_string}
                                            loading="lazy"
                                            decoding="async"
                                            width={640}
                                            height={480}
                                        />
                                        : <div className="publicListings__noImg">No photo yet</div>
                                    }
                                    <span className={`publicListings__badge publicListings__badge--${card.status === "sstc" ? "offer" : "sale"}`}>
                                        {statusLabel(card.status)}
                                    </span>
                                    {card.reason_for_sale === "probate" &&
                                        <span className="publicListings__badge publicListings__badge--probate">Probate</span>
                                    }
                                </div>
                            </a>
                            <div className="publicListings__cardBody">
                                <div className="publicListings__price">
                                    {formatPrice(card)}
                                    {card.price !== null && card.price_qualifier &&
                                        <span className="publicListings__qualifier"> {snakeCaseToCopyText(card.price_qualifier)}</span>
                                    }
                                </div>
                                <a className="publicListings__addr" href={`/listings/${card.id}`}>
                                    {card.address_short_string || "Address available on request"}
                                </a>
                                {facts(card).length > 0 &&
                                    <div className="publicListings__facts">
                                        {facts(card).map((f, i) => <span key={i}>{f}</span>)}
                                    </div>
                                }
                                <div className="publicListings__cardCta">
                                    <AnchorButton
                                        label="Book a viewing"
                                        type="rooster"
                                        url={`/listings/${card.id}/book-a-viewing`}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {v.totalPages > 1 &&
                    <nav className="publicListings__pager" aria-label="Pagination">
                        {v.currentPage > 1 &&
                            <a className="publicListings__pagerLink" href={filterUrl(v, v.currentPage - 1)}>&lsaquo; Prev</a>
                        }
                        {Array.from({ length: v.totalPages }, (_, i) => i + 1).map((page) => (
                            <a
                                key={page}
                                className={`publicListings__pagerLink${page === v.currentPage ? " publicListings__pagerLink--on" : ""}`}
                                href={filterUrl(v, page)}
                                aria-current={page === v.currentPage ? "page" : undefined}
                            >
                                {page}
                            </a>
                        ))}
                        {v.currentPage < v.totalPages &&
                            <a className="publicListings__pagerLink" href={filterUrl(v, v.currentPage + 1)}>Next &rsaquo;</a>
                        }
                    </nav>
                }
            </div>

            <MarketingFooter view={v} />
        </div>
    );
};
